import type { BookingOrderData } from "@/api/aiChat";

// ════════════════════════════════════════════════════
//  业务预定订单消息卡片
// ════════════════════════════════════════════════════

/** task_params 中常见的字段中文名映射 */
const PARAM_LABEL: Record<string, string> = {
  city: "城市",
  cuisine: "菜系",
  dinner_date: "日期",
  dinner_time: "时间",
  guest_count: "人数",
  departure: "出发地",
  destination: "目的地",
  date: "日期",
  time: "时间",
  train_no: "车次",
  flight_no: "航班",
  hotel_name: "酒店",
  check_in: "入住",
  check_out: "离店",
  brand: "品牌",
  model: "车型",
  car_type: "车型",
  pickup_date: "取车",
  return_date: "还车",
  pickup_city: "取车城市",
  return_city: "还车城市",
  passengers: "人数",
  budget: "预算",
  scene: "场景",
  plan_name: "方案",
  days: "天数",
  people: "人数",
  route: "线路",
  guide_name: "导游",
  type: "类型",
};

/** 值 → 展示文本（对象/数组做安全降级，避免 [object Object]） */
function fmtValue(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) return v.map(fmtValue).filter(Boolean).join("、");
  if (typeof v === "object") {
    const o = v as Record<string, unknown>;
    // 常见嵌套：{name, value} / {label, value}
    const prefer = o.name ?? o.label ?? o.value ?? o.text;
    if (prefer != null && typeof prefer !== "object") return String(prefer);
    try {
      return Object.values(o).map(fmtValue).filter(Boolean).join(" ");
    } catch {
      return "";
    }
  }
  return String(v);
}

/** 单个参数标签配色（按 key 稳定分配，保证同一字段颜色一致） */
const TAG_PALETTE = [
  { background: "#ECFDF5", color: "#059669" },
  { background: "#FEF2F2", color: "#DC2626" },
  { background: "#EFF6FF", color: "#2563EB" },
  { background: "#F3E8FF", color: "#7C3AED" },
  { background: "#FFF7ED", color: "#EA580C" },
  { background: "#FDF2F8", color: "#DB2777" },
  { background: "#F0FDFA", color: "#0D9488" },
];

export default function OrderCard({ orderData }: { orderData: BookingOrderData }) {
  if (!orderData) return null;
  const tp = (orderData.task_params ?? {}) as Record<string, unknown>;

  // ★ 全量渲染 task_params（过滤空值与已单独展示的字段）
  const paramEntries = Object.entries(tp)
    .map(([k, v]) => [k, fmtValue(v)] as const)
    .filter(([k, v]) => v && k !== "order_no" && k !== "goods" && k !== "order_amount")
    .slice(0, 20);

  return (
    <div className="flex flex-col gap-0 rounded-xl w-full"
      style={{ background: "#FAFBFF", border: "1px solid #EEF0F6" }}
    >
      {/* 标题栏 */}
      <div
        className="flex items-center gap-2 px-3.5 py-2.5"
        style={{ background: "linear-gradient(135deg, #FEF3C7, #FDE68A)", borderBottom: "1px solid #EEF0F6" }}
      >
        <i className="ri-file-list-3-fill" style={{ color: "#D97706", fontSize: 15 }} />
        <span className="text-[12px] font-semibold" style={{ color: "#333" }}>订单已创建</span>
        <span className="text-[10px] px-2 py-0.5 rounded-full font-mono ml-auto"
          style={{ background: "#FDE68A", color: "#92400E" }}
        >{orderData.order_no}</span>
      </div>

      {/* 订单内容区 */}
      <div className="px-3.5 py-3 space-y-2.5">
        {/* 金额 */}
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400">订单金额</span>
          <span className="text-base font-bold" style={{ color: "#D97706" }}>¥{Number(orderData.order_amount).toLocaleString()}</span>
        </div>

        {/* 商品列表 */}
        {orderData.goods?.length > 0 && (
          <div className="rounded-lg p-2.5 space-y-1.5"
            style={{ background: "#FFFBEB", border: "1px solid #FDE68A" }}
          >
            <div className="text-[10px] font-semibold text-gray-500 mb-0.5">预订内容 ({orderData.goods_count}项)</div>
            {orderData.goods.map((g, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="truncate mr-2" style={{ color: "#78716C" }}>{g.name}</span>
                <span className="font-semibold text-gray-600 flex-shrink-0">×{g.number}</span>
              </div>
            ))}
          </div>
        )}

        {/* ★ 需求参数（全量渲染 task_params） */}
        {paramEntries.length > 0 && (
          <div className="rounded-lg p-2.5"
            style={{ background: "#FFF", border: "1px solid #F0F2F8" }}
          >
            <div className="text-[10px] font-semibold text-gray-500 mb-1.5">
              <i className="ri-list-check-2 mr-1" style={{ fontSize: 10, color: "#D97706" }} />需求信息
            </div>
            <div className="grid grid-cols-2 gap-x-2 gap-y-1.5">
              {paramEntries.map(([k, v], i) => {
                const palette = TAG_PALETTE[i % TAG_PALETTE.length];
                return (
                  <div key={k} className="flex items-center gap-1 min-w-0 text-[10px]">
                    <span className="flex-shrink-0 px-1 py-0.5 rounded"
                      style={palette}
                    >
                      {PARAM_LABEL[k] || k}
                    </span>
                    <span className="truncate" style={{ color: "#475569" }} title={v}>{v}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 提示 */}
        <div className="text-center py-1">
          <span className="text-[10px]" style={{ color: "#D97706" }}><i className="ri-time-line mr-1" />请及时确认并支付</span>
        </div>
      </div>
    </div>
  );
}
